import React from 'react';
import { ChevronDown, Palette } from 'lucide-react';
import { generateSecondaryColor, generateSecondaryColorWithOffset } from '../../utils/colorUtils';
import { FONT_FAMILIES } from '../../utils/branding';

export default function BrandingThemeTab({ form, update }) {
  // Ensure default values exist for the UI state
  const primaryColor = form.theme_color || '#3B82F6';
  const secondaryColor = form.secondary_color || '#64748B';
  const borderRadius = (form.border_radius === 0 || form.border_radius) ? form.border_radius : 12;
  const themeMode = form.theme_mode || 'light';
  const fontFamily = form.font_family || 'Inter';

  const handlePrimaryChange = (color) => {
    update('theme_color', color);
    const newSecondary = generateSecondaryColor(color);
    update('secondary_color', newSecondary);
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div>
        <h3 className="font-bold text-gray-900 dark:text-white text-lg">Theme Settings</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configure colors, typography, and layout preferences.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colors Section */}
        <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white">Colors</h4>
            <p className="text-sm text-gray-500 mt-1">Choose the primary color for your brand.</p>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Primary Color</label>
            <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-lg p-1.5 focus-within:ring-2 focus-within:ring-blue-500">
              <label 
                className="w-8 h-8 rounded-md shrink-0 border border-black/10 cursor-pointer relative overflow-hidden block" 
                style={{ backgroundColor: primaryColor }}
              >
                <input 
                    type="color" 
                    value={primaryColor} 
                    onChange={(e) => handlePrimaryChange(e.target.value)}
                    className="absolute opacity-0 w-[200%] h-[200%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                 />
              </label>
              <input 
                type="text" 
                value={primaryColor} 
                onChange={(e) => handlePrimaryChange(e.target.value)}
                className="flex-1 bg-transparent border-0 focus:ring-0 px-3 text-sm font-medium text-gray-700 dark:text-gray-200 uppercase"
              />
              <label className="relative flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-600 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 overflow-hidden">
                 <input 
                    type="color" 
                    value={primaryColor} 
                    onChange={(e) => handlePrimaryChange(e.target.value)}
                    className="absolute opacity-0 w-[200%] h-[200%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                 />
                 <Palette className="w-4 h-4 relative z-10 pointer-events-none" />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">Used for primary buttons, links, and active states.</p>
          </div>

          <div className="space-y-2 mt-4">
            <div className="flex justify-between items-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Secondary Color</label>
              <span className="text-[10px] text-gray-400 font-mono uppercase">{secondaryColor}</span>
            </div>
            <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-lg p-1.5 focus-within:ring-2 focus-within:ring-blue-500">
              <label 
                className="w-8 h-8 rounded-md shrink-0 border border-black/10 cursor-pointer relative overflow-hidden block" 
                style={{ backgroundColor: secondaryColor }}
              >
                <input 
                    type="color" 
                    value={secondaryColor} 
                    onChange={(e) => update('secondary_color', e.target.value)}
                    className="absolute opacity-0 w-[200%] h-[200%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                 />
              </label>
              <input 
                type="text" 
                value={secondaryColor} 
                onChange={(e) => update('secondary_color', e.target.value)}
                className="flex-1 bg-transparent border-0 focus:ring-0 px-3 text-sm font-medium text-gray-700 dark:text-gray-200 uppercase"
              />
              <label className="relative flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-600 cursor-pointer rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 overflow-hidden">
                 <input 
                    type="color" 
                    value={secondaryColor} 
                    onChange={(e) => update('secondary_color', e.target.value)}
                    className="absolute opacity-0 w-[200%] h-[200%] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                 />
                 <Palette className="w-4 h-4 relative z-10 pointer-events-none" />
              </label>
            </div>
            
            <div className="pt-2">
               <div className="flex justify-between items-center mb-1">
                 <span className="text-[10px] text-gray-500 font-medium">Refine Secondary Shade</span>
               </div>
               <input 
                 type="range" 
                 min="-40" 
                 max="40" 
                 defaultValue="0"
                 onChange={(e) => {
                   const offset = parseInt(e.target.value);
                   const newSecondary = generateSecondaryColorWithOffset(primaryColor, offset);
                   update('secondary_color', newSecondary);
                 }}
                 className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
               />
            </div>
            <p className="text-xs text-gray-400 italic mt-2 leading-tight">Adjust the slider to increase/decrease darkness based on your primary color.</p>
          </div>
        </div>

        {/* Typography Section */}
        <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white">Typography</h4>
            <p className="text-sm text-gray-500 mt-1">Choose the default font family for your platform.</p>
          </div>

          <div className="space-y-4">
            <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Font Family</label>
               <div className="relative">
                 <select 
                   value={fontFamily}
                   onChange={(e) => update('font_family', e.target.value)}
                   className="w-full appearance-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm rounded-lg px-3 py-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   {FONT_FAMILIES.map(font => (
                     <option key={font} value={font}>{font}</option>
                   ))}
                 </select>
                 <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
               </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-100 dark:border-gray-700">
               <div className="flex items-end gap-3 mb-2">
                 <span className="text-xl font-bold" style={{ fontFamily }}>Aa</span>
                 <span className="text-sm font-medium" style={{ fontFamily }}>{fontFamily}</span>
               </div>
               <p className="text-xs text-gray-600 dark:text-gray-400" style={{ fontFamily }}>The quick brown fox jumps over the lazy dog.</p>
            </div>
            <p className="text-xs text-gray-500">Applied across the platform.</p>
          </div>
        </div>

        {/* Border Radius Section */}
        <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white">Button Border Radius</h4>
            <p className="text-sm text-gray-500 mt-1">Adjust the roundness of UI elements (buttons, inputs, cards).</p>
          </div>

          <div className="space-y-6">
             <div className="flex items-center gap-4">
                <input 
                  type="range" 
                  min="0" 
                  max="16" 
                  value={borderRadius} 
                  onChange={(e) => update('border_radius', parseInt(e.target.value))}
                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="w-14 shrink-0 text-center py-1 bg-gray-50 border rounded-md text-xs font-semibold">
                   {borderRadius} px
                </div>
             </div>
             
             <div className="flex justify-between text-[10px] text-gray-400 px-1 -mt-4">
                <span>0px</span>
                <span>4px</span>
                <span>8px</span>
                <span>12px</span>
                <span>16px</span>
             </div>

             <div>
                <label className="block text-xs font-medium text-gray-700 mb-3">Preview</label>
                <div className="flex gap-3 items-center">
                   <button 
                     className="px-4 py-2 bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100"
                     style={{ borderRadius: `${borderRadius}px` }}
                   >
                      Button
                   </button>
                   <input 
                     type="text" 
                     placeholder="Input Field" 
                     className="w-24 px-3 py-2 border border-gray-200 text-xs bg-white"
                     style={{ borderRadius: `${borderRadius}px` }}
                     readOnly
                   />
                   <div 
                     className="flex-1 px-3 py-2 border border-gray-200 text-xs bg-white text-gray-500 text-center"
                     style={{ borderRadius: `${borderRadius}px` }}
                   >
                      Card
                   </div>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* Entire Theme Section */}
      <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white">Entire Theme</h4>
            <p className="text-sm text-gray-500 mt-1">Choose the default theme appearance for your application.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
             {/* System Theme Card */}
             <div 
                className={`relative border rounded-xl p-4 flex gap-4 cursor-pointer transition-all ${themeMode === 'system' ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'}`}
                onClick={() => update('theme_mode', 'system')}
             >
                <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${themeMode === 'system' ? 'border-blue-500' : 'border-gray-300 dark:border-gray-600'}`}>
                   {themeMode === 'system' && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                </div>
                
                <div className="w-28 h-20 bg-gradient-to-br from-gray-50 to-[#0f172a] border border-gray-300 dark:border-gray-600 rounded flex flex-col p-2 gap-1.5 shrink-0 overflow-hidden shadow-sm">
                   <div className="h-3 w-12 bg-blue-500 rounded-full mb-1" />
                   <div className="flex gap-2">
                      <div className="w-3 h-3 bg-gray-400 rounded-sm" />
                      <div className="w-16 h-3 bg-gray-400 rounded-sm" />
                   </div>
                   <div className="flex gap-2">
                      <div className="w-3 h-3 bg-gray-400 rounded-sm" />
                      <div className="w-10 h-3 bg-gray-400 rounded-sm" />
                   </div>
                   <div className="flex gap-2 mt-1">
                      <div className="w-full h-8 bg-white/30 border border-gray-400/50 rounded-sm" />
                   </div>
                </div>

                <div>
                   <h5 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">System Theme</h5>
                   <p className="text-xs text-gray-500 dark:text-gray-400">Automatically syncs with OS settings.</p>
                </div>
             </div>

             {/* Light Theme Card */}
             <div 
                className={`relative border rounded-xl p-4 flex gap-4 cursor-pointer transition-all ${themeMode === 'light' ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                onClick={() => update('theme_mode', 'light')}
             >
                <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${themeMode === 'light' ? 'border-blue-500' : 'border-gray-300'}`}>
                   {themeMode === 'light' && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                </div>
                
                <div className="w-28 h-20 bg-gray-50 border rounded flex flex-col p-2 gap-1.5 shrink-0 overflow-hidden shadow-sm">
                   <div className="h-3 w-12 bg-blue-500 rounded-full mb-1" />
                   <div className="flex gap-2">
                      <div className="w-3 h-3 bg-gray-300 rounded-sm" />
                      <div className="w-16 h-3 bg-gray-200 rounded-sm" />
                   </div>
                   <div className="flex gap-2">
                      <div className="w-3 h-3 bg-gray-300 rounded-sm" />
                      <div className="w-10 h-3 bg-gray-200 rounded-sm" />
                   </div>
                   <div className="flex gap-2 mt-1">
                      <div className="w-full h-8 bg-white border rounded-sm" />
                   </div>
                </div>

                <div>
                   <h5 className="font-semibold text-gray-900 text-sm mb-1">Light Theme</h5>
                   <p className="text-xs text-gray-500">Clean and bright theme for a light experience.</p>
                </div>
             </div>

             {/* Dark Theme Card */}
             <div 
                className={`relative border rounded-xl p-4 flex gap-4 cursor-pointer transition-all ${themeMode === 'dark' ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'}`}
                onClick={() => update('theme_mode', 'dark')}
             >
                <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${themeMode === 'dark' ? 'border-blue-500' : 'border-gray-300 dark:border-gray-600'}`}>
                   {themeMode === 'dark' && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                </div>
                
                <div className="w-28 h-20 bg-[#0f172a] border border-gray-700 rounded flex flex-col p-2 gap-1.5 shrink-0 overflow-hidden shadow-sm">
                   <div className="h-3 w-12 bg-blue-500 rounded-full mb-1" />
                   <div className="flex gap-2">
                      <div className="w-3 h-3 bg-gray-600 rounded-sm" />
                      <div className="w-16 h-3 bg-gray-700 rounded-sm" />
                   </div>
                   <div className="flex gap-2">
                      <div className="w-3 h-3 bg-gray-600 rounded-sm" />
                      <div className="w-10 h-3 bg-gray-700 rounded-sm" />
                   </div>
                   <div className="flex gap-2 mt-1">
                      <div className="w-full h-8 bg-gray-800 border border-gray-700 rounded-sm" />
                   </div>
                </div>

                <div>
                   <h5 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">Dark Theme</h5>
                   <p className="text-xs text-gray-500 dark:text-gray-400">Modern and sleek theme for a dark experience.</p>
                </div>
             </div>
          </div>
      </div>
    </div>
  );
}
